import { Play, Square, Loader2, AlertTriangle, RectangleHorizontal, RectangleVertical, Gauge, Zap } from "lucide-react";

const ORIENTATIONS = [
    { id: "landscape", label: "16:9", Icon: RectangleHorizontal },
    { id: "portrait", label: "9:16", Icon: RectangleVertical },
];

const SPEEDS = [
    { fast: false, label: "STD · 2 cr/s", Icon: Gauge, testid: "speed-standard-button" },
    { fast: true, label: "FAST · 4 cr/s", Icon: Zap, testid: "speed-fast-button" },
];

const ToggleRow = ({ label, testid, children }) => (
    <div className="flex items-center gap-3" data-testid={testid}>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 w-14">
            {label}
        </span>
        <div className="flex flex-1 border border-white/10">{children}</div>
    </div>
);

const toggleBtnClass = (active, disabled) =>
    `flex-1 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] py-2 transition-colors ${
        active ? "bg-blaze/15 text-blaze border-blaze" : "text-white/50 hover:text-white"
    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`;

const OrientationToggle = ({ orientation, onChange, disabled }) => (
    <ToggleRow label="Output" testid="orientation-toggle">
        {ORIENTATIONS.map(({ id, label, Icon }) => (
            <button
                key={id}
                onClick={() => onChange(id)}
                disabled={disabled}
                aria-pressed={orientation === id}
                data-testid={`orientation-${id}-button`}
                className={toggleBtnClass(orientation === id, disabled)}
            >
                <Icon className="w-3.5 h-3.5" />
                {label}
            </button>
        ))}
    </ToggleRow>
);

const SpeedToggle = ({ fastMode, onChange, disabled }) => (
    <ToggleRow label="Speed" testid="speed-toggle">
        {SPEEDS.map(({ fast, label, Icon, testid }) => (
            <button
                key={testid}
                onClick={() => onChange(fast)}
                disabled={disabled}
                aria-pressed={fastMode === fast}
                data-testid={testid}
                className={toggleBtnClass(fastMode === fast, disabled)}
            >
                <Icon className="w-3.5 h-3.5" />
                {label}
            </button>
        ))}
    </ToggleRow>
);

const ErrorBanner = ({ message }) => (
    <div
        className="border border-blaze/40 bg-blaze/10 p-4 flex items-start gap-3"
        data-testid="error-banner"
    >
        <AlertTriangle className="w-4 h-4 text-blaze flex-shrink-0 mt-0.5" />
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-blaze mb-1">
                Error
            </div>
            <div className="text-xs text-white/80 leading-relaxed">{message}</div>
        </div>
    </div>
);

const StartStopButtons = ({ isLive, isConnecting, onStart, onStop }) => (
    <div className="flex gap-3">
        {!isLive && !isConnecting && (
            <button
                onClick={onStart}
                className="flex-1 bg-blaze hover:bg-blazeHover text-white font-mono uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                data-testid="start-stream-button"
            >
                <Play className="w-4 h-4 fill-current" />
                Start
            </button>
        )}
        {isConnecting && (
            <button
                disabled
                className="flex-1 bg-electric/80 text-white font-mono uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2"
                data-testid="connecting-button"
            >
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
            </button>
        )}
        {isLive && (
            <button
                onClick={onStop}
                className="flex-1 bg-transparent border border-white/30 hover:border-blaze hover:text-blaze text-white font-mono uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 transition-colors"
                data-testid="stop-stream-button"
            >
                <Square className="w-4 h-4 fill-current" />
                Stop
            </button>
        )}
    </div>
);

export const SessionControls = ({
    isLive,
    isConnecting,
    onStart,
    onStop,
    errorMessage,
    orientation,
    onOrientationChange,
    fastMode,
    onFastModeChange,
}) => (
    <>
        <OrientationToggle
            orientation={orientation}
            onChange={onOrientationChange}
            disabled={isLive || isConnecting}
        />
        <SpeedToggle
            fastMode={fastMode}
            onChange={onFastModeChange}
            disabled={isLive || isConnecting}
        />
        <StartStopButtons isLive={isLive} isConnecting={isConnecting} onStart={onStart} onStop={onStop} />
        {errorMessage && <ErrorBanner message={errorMessage} />}
    </>
);
